import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'

import TextArea from 'modules/core/components/Inputs/TextArea'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import useBulkApproveRejectFlows from 'modules/approvals/hooks/useBulkApproveRejectFlows'

interface Props {
  id: string
  requestId: string
  removeRequestById: (id: string) => void
  onSuccess?: (action: string, reason?: string) => void
}

const ActionButtons = ({ id, requestId, removeRequestById, onSuccess }: Props) => {
  const {
    access: { id: currentAccessId },
  } = useCurrentSession()

  const [rejectModal, setRejectModal] = React.useState<boolean>(false)
  const { approve, reject, disabled } = useBulkApproveRejectFlows()

  const form = useLazyRef(
    () =>
      new Form(
        {
          reason: '',
        },
        {
          reason: 'string',
        }
      )
  ).current

  const onComplete = (action: string) => {
    removeRequestById(requestId)
    track(trackingEvents.inbox.requests.quickActionTriggered, {
      id: requestId,
      action: action,
    })

    if (onSuccess) onSuccess(action, form.get('reason').value)
  }

  const disabledButton = disabled

  const actionData = {
    accessId: currentAccessId,
    materializedFlowId: Number(id),
  }

  // We don't know why reason gets deleted from actionData when sending the request, I
  // invite someone to investigate this issue. Meanwhile, this fixes the problem
  // of not sending the rejection reason
  const onReject = () => {
    const reason = form.get('reason').value
    return reason
      ? reject({ ...actionData, reason }, () => onComplete('bulk_reject'))
      : reject(actionData, () => onComplete('bulk_reject'))
  }

  return (
    <Box flexDirection="row" gap="s4">
      <Button type="negative" disabled={disabledButton} onClick={() => setRejectModal(true)}>
        {i18n.t('common.reject')}
      </Button>
      <Button
        type="positive"
        disabled={disabledButton}
        onClick={() => approve(actionData, () => onComplete('bulk_approve'))}
      >
        {i18n.t('common.approve')}
      </Button>

      {rejectModal && (
        <Modal
          illustration="add-block"
          title={i18n.t('settings.approvals.approval_request_view.reject_leave_modal.title')}
          description={i18n.t(
            'settings.approvals.approval_request_view.reject_leave_modal.description'
          )}
          onSubmit={onReject}
          onClose={() => setRejectModal(false)}
          footer={
            <>
              <Button
                disabled={disabledButton}
                type="secondary"
                onClick={() => setRejectModal(false)}
              >
                {i18n.t('common.cancel')}
              </Button>
              <Button disabled={disabledButton} submit>
                {i18n.t('dashboard.todo.approvals.general.reject.button')}
              </Button>
            </>
          }
        >
          <TextArea
            field={form.get('reason')}
            label={i18n.t('dashboard.todo.approvals.general.reject.label')}
          />
        </Modal>
      )}
    </Box>
  )
}

export default ActionButtons
